import { useEffect } from "react";
import { motion } from "framer-motion";
import { BarChart3, Shield, TrendingUp } from "lucide-react";
import SimCollectLogo from "../logo/SimCollectLogo";

const GLASS_STATS = [
  { value: "98%", label: "Recovery SLA", icon: Shield },
  { value: "3.2x", label: "Recovery lift", icon: TrendingUp },
  { value: "24/7", label: "Monitoring", icon: BarChart3 },
];

function GlassCard({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      className={`
        rounded-xl border border-white/10 bg-white/[0.06] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]
        backdrop-blur-md ${className}
      `}
    >
      {children}
    </motion.div>
  );
}

function RecoveryChart() {
  return (
    <svg
      viewBox="0 0 400 120"
      fill="none"
      className="h-24 w-full max-w-md opacity-40"
      aria-hidden="true"
    >
      <motion.path
        d="M0 90 L60 75 L120 82 L180 45 L240 55 L300 20 L360 35 L400 10"
        stroke="#E5B80B"
        strokeWidth="2"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 2, ease: "easeOut", delay: 0.4 }}
      />
      <motion.path
        d="M0 100 L400 100"
        stroke="white"
        strokeWidth="0.5"
        strokeOpacity="0.15"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1, delay: 0.2 }}
        style={{ transformOrigin: "left" }}
      />
      {[60, 120, 180, 240, 300, 360].map((x, i) => (
        <motion.circle
          key={x}
          cx={x}
          cy={[75, 82, 45, 55, 20, 35][i]}
          r="3"
          fill="#E5B80B"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.8 + i * 0.15 }}
        />
      ))}
    </svg>
  );
}

function StepIndicator({ steps, currentStep }) {
  if (!steps?.length) return null;

  return (
    <div className="mb-4 flex items-center gap-3">
      {steps.map((step, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === currentStep;
        const isDone = stepNum < currentStep;

        return (
          <div key={step.label} className="flex items-center gap-3">
            {i > 0 && (
              <div
                className={`h-px w-8 sm:w-10 ${isDone ? "bg-accent" : "bg-primary/15"}`}
              />
            )}
            <div className="flex items-center gap-2">
              <span
                className={`
                  flex h-6 w-6 items-center justify-center text-[10px] font-bold
                  transition-colors duration-300
                  ${isActive ? "bg-accent text-primary" : isDone ? "bg-accent/20 text-accent-dark" : "bg-primary/8 text-primary/35"}
                `}
              >
                {isDone ? "✓" : stepNum}
              </span>
              <span
                className={`hidden text-[10px] font-medium uppercase tracking-wider sm:block ${
                  isActive ? "text-primary" : "text-primary/35"
                }`}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AuthLayout({
  children,
  title,
  subtitle,
  badge = "Provider Portal",
  steps,
  currentStep,
}) {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);

  return (
    <div className="relative flex h-dvh max-h-dvh flex-col overflow-hidden bg-[#faf6ef]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(59,47,41,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59,47,41,0.06) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
        }}
      />

      <div className="pointer-events-none absolute left-1/2 top-0 h-[600px] w-[900px] -translate-x-1/2 rounded-full bg-collect/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-accent/20 blur-[100px]" />

      <div
        className="pointer-events-none absolute -left-4 top-1/4 select-none font-black uppercase leading-none tracking-tighter text-primary/[0.04] sm:-left-8"
        aria-hidden="true"
      >
        <p className="text-[clamp(4rem,15vw,11rem)]">Recover</p>
        <p className="text-[clamp(4rem,15vw,11rem)] text-accent/[0.08]">Collect</p>
      </div>

      <header className="relative z-20 flex shrink-0 items-center px-6 py-4 sm:px-10">
        <SimCollectLogo theme="light" iconSize={44} variant="wordmark" />
      </header>

      <main className="relative z-10 flex min-h-0 flex-1 items-center">
        <div className="mx-auto flex w-full max-w-8xl items-center gap-8 px-6 py-3 lg:justify-between lg:gap-12 lg:px-10">
          <motion.aside
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="hidden max-w-md shrink-0 lg:block"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent-dark">
              Institution portal
            </p>
            <h1 className="mt-3 text-3xl font-bold leading-tight text-primary xl:text-4xl">
              Your portfolios and recoveries in{" "}
              <span className="text-accent-dark">one place</span>
            </h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-primary/55">
              Sign in with your institution&apos;s primary email — one account per client, no separate team logins.
            </p>

            <GlassCard className="mt-5 border-primary/10 bg-white/75 p-4 shadow-sm" delay={0.2}>
              <div className="grid grid-cols-3 divide-x divide-primary/10">
                {GLASS_STATS.map((stat) => (
                  <div key={stat.label} className="px-2 first:pl-0 last:pr-0">
                    <stat.icon className="mb-1.5 h-4 w-4 text-accent" />
                    <p className="text-base font-bold text-primary">{stat.value}</p>
                    <p className="mt-0.5 text-[10px] leading-tight text-primary/45">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </GlassCard>

            <div className="mt-5">
              <RecoveryChart />
              <p className="mt-2 text-xs text-primary/35">
                Real-time recovery intelligence · Portfolio analytics
              </p>
            </div>
          </motion.aside>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
            className="relative mx-auto w-full max-w-[440px] shrink-0 p-2 lg:mx-0"
          >
            {/* Gold corner brackets — sit in wrapper padding, not clipped */}
            <div
              className="pointer-events-none absolute left-0 top-0 z-20 h-8 w-8 border-l-2 border-t-2 border-accent"
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute bottom-0 right-0 z-20 h-8 w-8 border-b-2 border-r-2 border-accent"
              aria-hidden="true"
            />

            <div className="relative rounded-xl border border-primary/10 bg-[#FDFCFA] px-6 py-5 shadow-[0_24px_48px_-16px_rgba(59,47,41,0.18)] sm:px-7 sm:py-6">
              <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-collect via-accent to-collect" />

              {badge && (
                <span className="inline-block bg-primary/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">
                  {badge}
                </span>
              )}

              <StepIndicator steps={steps} currentStep={currentStep} />

              <div className={steps?.length ? "mt-2" : "mt-3"}>
                <h2 className="text-xl font-bold leading-tight tracking-tight text-primary sm:text-[1.35rem]">
                  {title}
                </h2>
                {subtitle && (
                  <p className="mt-1.5 text-sm leading-snug text-primary/50">
                    {subtitle}
                  </p>
                )}
              </div>

              <div className="my-4 h-px bg-gradient-to-r from-transparent via-primary/10 to-transparent" />

              {children}
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="relative z-10 shrink-0 px-6 py-2 text-center text-xs text-primary/35 sm:px-10 sm:text-left">
        © {new Date().getFullYear()} SimCollect
      </footer>
    </div>
  );
}
